import qs from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useDebouncedEffect } from "../../../../../hooks/useDebounceEffect";
import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import { hasAllRequiredFields, hasChanges } from "../../../../ui library/helpers/functions";
import { getHiddenDocumentTypeKeys, getSelectableDocTypes } from "../../utils/documentTypeOptions";
import { initialDataMapper, validateDocType } from "../utils/functions";
import {
  DYNAMIC_FIELD_INITIAL_LIMIT,
  DYNAMIC_FIELD_LIMIT_INCREMENT,
  dynamicFieldOptionsMap,
  fieldMappings,
  initialDynamicFieldFirstLoad,
  initialDynamicFieldInputValues,
  initialDynamicFieldSearch,
  initialObj,
  initialOpts,
  initialRequiredFields,
  SEARCH_DEBOUNCE_DELAY,
} from "../utils/strings";

const useDocumentState = ({
  externalOnSubmit,
  externalRefresh,
  initialSelectedDocument,
  isFormModule,
  onClose,
  open,
  prefillOverrides = null,
  selectedDocument,
  shouldHydrateSelectedDocument = false,
}) => {
  const { id } = useParams();
  const documentRepository = useService("repositories/document-repository");
  const documentValidateRepository = useService("repositories/document-validate-repository");
  const enumRepository = useService("repositories/enum-repository");
  const fieldOptionRepository = useService("repositories/field-option-repository");
  const [formData, setFormData] = useState(initialObj);
  const formDataRef = useRef(initialObj);
  const [initialFormData, setInitialFormData] = useState(initialObj);
  const [options, setOptions] = useState(initialOpts);
  const [requiredFields, setRequiredFields] = useState(initialRequiredFields);
  const [supportedFiles, setSupportedFiles] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHydratingDocument, setIsHydratingDocument] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [fieldSearchValues, setFieldSearchValues] = useState(initialDynamicFieldSearch);
  const [fieldInputValues, setFieldInputValues] = useState(initialDynamicFieldInputValues);
  const [fieldInitialLoadState, setFieldInitialLoadState] = useState(initialDynamicFieldFirstLoad);
  const [hydratedSelectedDocument, setHydratedSelectedDocument] = useState(null);
  const hydratedDocumentIdRef = useRef(null);
  const prefillAppliedRef = useRef(false);
  const allowSave = useMemo(
    () => hasAllRequiredFields({ requiredFields, formData }),
    [requiredFields, formData]
  );
  const hasChanged = useMemo(
    () => hasChanges(initialFormData, formData),
    [initialFormData, formData]
  );

  const resolvedSelectedDocument = useMemo(() => {
    if (shouldHydrateSelectedDocument) {
      return hydratedSelectedDocument || selectedDocument;
    }
    return selectedDocument;
  }, [hydratedSelectedDocument, selectedDocument, shouldHydrateSelectedDocument]);

  const resolvedInitialDocument = useMemo(() => {
    if (initialSelectedDocument) {
      return shouldHydrateSelectedDocument
        ? hydratedSelectedDocument || initialSelectedDocument
        : initialSelectedDocument;
    }
    if (shouldHydrateSelectedDocument) {
      return hydratedSelectedDocument || selectedDocument;
    }
    return null;
  }, [
    hydratedSelectedDocument,
    initialSelectedDocument,
    selectedDocument,
    shouldHydrateSelectedDocument,
  ]);

  const currentDocumentType = useMemo(
    () => initialDataMapper(resolvedSelectedDocument || resolvedInitialDocument).documentType,
    [resolvedInitialDocument, resolvedSelectedDocument]
  );

  const caseId = useMemo(
    () =>
      id ||
      resolvedSelectedDocument?.case ||
      resolvedInitialDocument?.case ||
      selectedDocument?.case ||
      initialSelectedDocument?.case,
    [
      id,
      initialSelectedDocument?.case,
      resolvedInitialDocument?.case,
      resolvedSelectedDocument?.case,
      selectedDocument?.case,
    ]
  );

  const normalizedPrefillOverrides = useMemo(() => {
    if (!prefillOverrides) {
      return null;
    }
    const normalizeEntry = (entry) => {
      if (!entry) {
        return null;
      }
      const id =
        entry.id ??
        entry.value ??
        entry.key ??
        entry.pk ??
        entry.provider_id ??
        entry.document_type;
      const label = entry.label ?? entry.name ?? entry.display_name ?? null;
      if (id === null && !label) {
        return null;
      }
      return { id, label };
    };

    const next = {
      documentType: normalizeEntry(prefillOverrides.documentType),
      source: normalizeEntry(prefillOverrides.source),
      provider: normalizeEntry(prefillOverrides.provider),
      referralFrom: normalizeEntry(prefillOverrides.referralFrom),
      referralTo: normalizeEntry(prefillOverrides.referralTo),
    };
    const hasAny = Object.values(next).some(Boolean);
    return hasAny ? next : null;
  }, [prefillOverrides]);

  useEffect(() => {
    if (!shouldHydrateSelectedDocument || !open || !selectedDocument?.id) {
      setHydratedSelectedDocument(null);
      hydratedDocumentIdRef.current = null;
      setIsHydratingDocument(false);
      return;
    }

    if (hydratedDocumentIdRef.current === selectedDocument.id) {
      return;
    }

    let isMounted = true;
    const hydrateSelectedDoc = async () => {
      setIsHydratingDocument(true);
      try {
        const fullDocument = await documentRepository.findRecord(selectedDocument.id);
        if (!isMounted) {
          return;
        }
        setHydratedSelectedDocument(fullDocument);
        hydratedDocumentIdRef.current = fullDocument?.id ?? selectedDocument.id;
      } catch (error) {
        if (!isMounted) {
          return;
        }
        handleError(error);
        setHydratedSelectedDocument(selectedDocument);
      } finally {
        if (isMounted) {
          setIsHydratingDocument(false);
        }
      }
    };

    hydrateSelectedDoc();

    return () => {
      isMounted = false;
    };
  }, [documentRepository, open, selectedDocument?.id, shouldHydrateSelectedDocument]);

  useEffect(() => {
    if (resolvedInitialDocument) {
      const initialMappedData = initialDataMapper(resolvedInitialDocument);
      setInitialFormData(initialMappedData);
    }
    if (resolvedSelectedDocument) {
      const mappedData = initialDataMapper(resolvedSelectedDocument);
      setFormData(mappedData);
      if (!resolvedInitialDocument) {
        setInitialFormData(mappedData);
      }
    }
    if (!resolvedSelectedDocument && !resolvedInitialDocument) {
      setFormData(initialObj);
      setInitialFormData(initialObj);
    }
    resetDynamicFieldStates();
  }, [resolvedInitialDocument, resolvedSelectedDocument]);

  useEffect(() => {
    prefillAppliedRef.current = false;
  }, [normalizedPrefillOverrides, resolvedInitialDocument, resolvedSelectedDocument]);

  useEffect(() => {
    if (!normalizedPrefillOverrides || prefillAppliedRef.current) {
      return;
    }

    const { nextFormData, nextInitialFormData, nextOptions, applied } = buildPrefillWithOverrides({
      currentFormData: formData,
      currentInitialFormData: initialFormData,
      overrides: normalizedPrefillOverrides,
      options,
    });

    if (!applied) {
      return;
    }

    prefillAppliedRef.current = true;
    if (nextOptions) {
      setOptions(nextOptions);
    }
    setFormData(nextFormData);
    setInitialFormData(nextInitialFormData);
  }, [formData, initialFormData, normalizedPrefillOverrides, options]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      try {
        const [optsResponse, documentValidation, fieldOptions] = await Promise.all([
          documentRepository.getOptions(),
          documentValidateRepository.get(),
          fieldOptionRepository.query({
            content_type: "document",
            field_name: "document_type",
            limit: 100,
          }),
        ]);
        const selectableDocTypes = getSelectableDocTypes(
          optsResponse?.docTypeOptions,
          getHiddenDocumentTypeKeys(fieldOptions),
          currentDocumentType
        );
        const mappedDocTypes =
          selectableDocTypes?.map((opt) => ({
            id: opt.value,
            label: opt.display_name,
          })) || [];

        const mappedDocSources =
          optsResponse?.sourceOptions?.map((opt) => ({
            id: opt.value,
            label: opt.display_name,
          })) || [];

        setOptions((prev) => ({
          ...prev,
          docTypeOptions: mappedDocTypes,
          sourceOptions: mappedDocSources,
          documentValidation,
          maxFileSize: documentValidation?.maxFileSize,
        }));
      } catch (error) {
        handleError(error);
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, [
    caseId,
    currentDocumentType,
    documentRepository,
    documentValidateRepository,
    fieldOptionRepository,
  ]);

  // Validate document type
  useEffect(() => {
    if (formData.documentType !== null && options.documentValidation) {
      validateDocType(
        formData.documentType,
        options.documentValidation,
        setSupportedFiles,
        setRequiredFields
      );
    } else {
      clearDocType();
    }
  }, [formData.documentType, options.documentValidation]);

  useDebouncedEffect(
    () => {
      debugger;
      fetchDynamicFieldOptions({
        fieldKey: "provider",
        allowCaseId: true,
        searchValue: fieldSearchValues.referralFrom,
        customURL: "core/dynamic-options/related-providers/",
        queryParams: { can_edit: true },
      });
    },
    [fieldSearchValues.provider, caseId, formData.provider],
    SEARCH_DEBOUNCE_DELAY
  );

  useDebouncedEffect(
    () => {
      fetchDynamicFieldOptions({
        fieldKey: "referralFrom",
        allowCaseId: true,
        searchValue: fieldSearchValues.referralFrom,
        customURL: "core/dynamic-options/related-providers/",
        queryParams: { can_edit: true },
      });
    },
    [fieldSearchValues.referralFrom, caseId, formData.referralFrom],
    SEARCH_DEBOUNCE_DELAY
  );

  useDebouncedEffect(
    () => {
      fetchDynamicFieldOptions({
        fieldKey: "referralTo",
        searchValue: fieldSearchValues.referralTo,
        allowCaseId: false,
      });
    },
    [fieldSearchValues.referralTo, caseId, formData.referralTo],
    SEARCH_DEBOUNCE_DELAY
  );

  function handleChange(key, value) {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (dynamicFieldOptionsMap[key]) {
      setFieldInputValues((prev) => ({
        ...prev,
        [key]: value ? null : "",
      }));
      setFieldSearchValues((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
  }

  function handleFileChange(file) {
    if (!file) {
      handleChange("file", null);
      handleChange("fileName", "");
      return;
    }

    const extension = "." + file?.name?.split(".").pop().toLowerCase();
    const allowedExtensions = supportedFiles.split(", ").map((ext) => ext.toLowerCase()) || [];

    if (!allowedExtensions.includes(extension)) {
      handleError({ message: `Invalid file type. Supported file types: ${supportedFiles}` });
      handleChange("file", null);
      handleChange("fileName", formData.fileName === null ? "" : null); // Necessary to clear the value on the DragAndDropContainer
      return;
    }
    handleChange("file", file);
    handleChange("fileName", file?.name || "");
  }

  async function handleSubmit() {
    setIsLoading(true);
    try {
      const body = {};
      const externalBody = {};

      // Process each field mapping
      fieldMappings(formData).forEach(
        ({
          key,
          bodyKey,
          externalKey,
          compareFunction = (initial, current) => initial !== current,
          extraFields = {},
        }) => {
          const hasChanged = compareFunction(initialFormData[key], formData[key]);

          if (hasChanged) {
            body[bodyKey] = formData[key];
            externalBody[externalKey] = formData[key];

            // Add any extra fields for this mapping
            Object.entries(extraFields).forEach(([extraKey, extraValue]) => {
              externalBody[extraKey] = extraValue;
            });
          }
        }
      );

      // If externalOnSubmit is provided, use it instead of the default save logic
      if (externalOnSubmit) {
        await externalOnSubmit(externalBody);
        setUploadSuccess(true);
        return;
      }

      // Save the file using upload
      if (body?.file) {
        await documentRepository.upload(
          { file: body.file },
          { urlPath: `documents/${formData.id}/` }
        );
        delete body.file;
      }

      // Save other changes using patch to avoid the FormData
      if (Object.keys(body).length > 0) {
        await documentRepository.patch(formData.id, body);
      }
      setUploadSuccess(true);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleUploadSuccess() {
    setUploadSuccess(false);
    externalRefresh?.();
    onClose();
    if (!isFormModule) {
      setFormData(initialObj);
      setInitialFormData(initialObj);
    }
    resetDynamicFieldStates();
  }

  function closeModal() {
    if (!isFormModule) {
      resetForm();
    }
    onClose();
  }

  function clearDocType() {
    if (initialFormData.documentType === null) {
      setSupportedFiles("");
      setRequiredFields(initialRequiredFields);
    }
  }

  function resetForm() {
    clearDocType();
    setFormData(initialFormData);
    resetDynamicFieldStates();
  }

  function handleError(error) {
    setUploadFailed(true);
    const errArr = ApiRequestErrorHandler(error?.response?.data || error);
    setErrorArray(errArr);
  }

  function handleFieldSearchChange(fieldKey, value) {
    if (!dynamicFieldOptionsMap[fieldKey]) {
      return;
    }
    const normalizedValue = typeof value === "string" ? value : "";
    setFieldSearchValues((prev) => ({
      ...prev,
      [fieldKey]: normalizedValue,
    }));
    setFieldInputValues((prev) => ({
      ...prev,
      [fieldKey]: normalizedValue,
    }));
  }

  async function fetchDynamicFieldOptions({
    fieldKey,
    searchValue = "",
    allowCaseId = true,
    currentLimit = DYNAMIC_FIELD_INITIAL_LIMIT,
    customURL = null,
    queryParams = {},
  }) {
    const optionsKey = dynamicFieldOptionsMap[fieldKey];
    if (!optionsKey) {
      return;
    }

    const selectedValue = formDataRef.current?.[fieldKey];
    const stringifiedSelected =
      selectedValue === null || selectedValue === undefined ? null : String(selectedValue);
    const isInitialLoad = fieldInitialLoadState[fieldKey] && !searchValue;
    const derivedSearch = searchValue || "";

    try {
      const effectiveLimit =
        derivedSearch && derivedSearch.length > 0 ? DYNAMIC_FIELD_INITIAL_LIMIT : currentLimit;
      const params = new URLSearchParams();
      if (allowCaseId && caseId) {
        params.append("case", caseId);
      }
      if (derivedSearch) {
        params.append("search", derivedSearch);
      }
      params.append("limit", effectiveLimit);

      const baseParams = Object.fromEntries(params.entries());

      const allParams = {
        ...baseParams,
        ...queryParams,
      };

      const queryString = qs.stringify(allParams, { skipNulls: true });
      const url = customURL ?? `core/dynamic-options/providers/`;
      const providersResp = await enumRepository.query({
        urlPath: `${url}${queryString ? `?${queryString}` : ""}`,
      });
      const count = await enumRepository.getTotalCount();
      const mappedProviders =
        providersResp?.map((provider) => {
          const id = provider?.key ?? provider?.id ?? provider?.value ?? null;
          const label = provider?.label ?? provider?.value ?? "";

          return {
            id,
            label,
          };
        }) || [];

      const hasSelectedOption =
        stringifiedSelected &&
        mappedProviders.some((provider) => String(provider.id) === stringifiedSelected);

      const hasMoreResults = count >= effectiveLimit && !derivedSearch;
      const shouldRetryInitialLoad =
        isInitialLoad && stringifiedSelected && !hasSelectedOption && hasMoreResults;

      if (shouldRetryInitialLoad) {
        const nextLimit = effectiveLimit + DYNAMIC_FIELD_LIMIT_INCREMENT;
        await fetchDynamicFieldOptions({
          fieldKey: fieldKey,
          searchValue: searchValue,
          allowCaseId: allowCaseId,
          currentLimit: nextLimit,
        });
        return;
      }
      const selectedOptionFromPrefill =
        stringifiedSelected && normalizedPrefillOverrides?.[fieldKey]?.label
          ? { id: selectedValue, label: normalizedPrefillOverrides[fieldKey].label }
          : null;

      setOptions((prev) => {
        const prevOptions = prev[optionsKey] || [];
        const selectedInPrev =
          stringifiedSelected && prevOptions.find((opt) => String(opt.id) === stringifiedSelected);
        const shouldInjectSelected = stringifiedSelected && !hasSelectedOption;
        const selectedToInject = selectedInPrev || selectedOptionFromPrefill;
        const optionsWithSelected =
          shouldInjectSelected && selectedToInject
            ? [selectedToInject, ...mappedProviders]
            : mappedProviders;

        return {
          ...prev,
          [optionsKey]: mergeOptionsById(optionsWithSelected, prevOptions),
        };
      });

      if (isInitialLoad) {
        setFieldInitialLoadState((prev) => ({
          ...prev,
          [fieldKey]: false,
        }));
      }
    } catch (error) {
      handleError(error);
    }
  }

  const fieldOptionsKeyMap = useMemo(
    () => ({
      documentType: "docTypeOptions",
      source: "sourceOptions",
      provider: dynamicFieldOptionsMap.provider,
      referralFrom: dynamicFieldOptionsMap.referralFrom,
      referralTo: dynamicFieldOptionsMap.referralTo,
    }),
    []
  );

  function buildPrefillWithOverrides({
    currentFormData,
    currentInitialFormData,
    overrides,
    options: currentOptions,
  }) {
    const nextFormData = { ...currentFormData };
    const nextInitialFormData = { ...currentInitialFormData };
    const nextOptionsPatch = {};
    let applied = false;

    Object.entries(overrides || {}).forEach(([fieldKey, override]) => {
      if (!override) {
        return;
      }
      const optionKey = fieldOptionsKeyMap[fieldKey];
      const optionList = optionKey ? currentOptions?.[optionKey] || [] : [];
      const targetId = findOverrideId(override, optionList);
      if (targetId === null || targetId === undefined || targetId === "") {
        return;
      }

      if (nextFormData[fieldKey] !== targetId || nextInitialFormData[fieldKey] !== targetId) {
        nextFormData[fieldKey] = targetId;
        nextInitialFormData[fieldKey] = targetId;
        applied = true;
      }

      if (optionKey && override.label) {
        const hasOption =
          optionList.some(
            (opt) =>
              String(opt?.id) === String(targetId) ||
              normalizeLabel(opt?.label) === normalizeLabel(override.label)
          ) || false;

        if (!hasOption) {
          nextOptionsPatch[optionKey] = [...optionList, { id: targetId, label: override.label }];
          applied = true;
        }
      }
    });

    const nextOptions =
      Object.keys(nextOptionsPatch).length > 0 ? { ...currentOptions, ...nextOptionsPatch } : null;

    return { nextFormData, nextInitialFormData, nextOptions, applied };
  }

  function normalizeLabel(label) {
    return (label || "").toString().trim().toLowerCase();
  }

  function findOverrideId(override, optionList = []) {
    if (override.id !== null && override.id !== undefined) {
      return override.id;
    }
    if (!override.label) {
      return null;
    }
    const match = optionList.find(
      (opt) => normalizeLabel(opt?.label) === normalizeLabel(override.label)
    );
    return match?.id ?? null;
  }

  function mergeOptionsById(primaryList, secondaryList) {
    const seen = new Set();
    const merged = [];
    [...(primaryList || []), ...(secondaryList || [])].forEach((opt) => {
      if (opt === null || opt === undefined || opt.id === null || opt.id === undefined) {
        return;
      }
      const key = String(opt.id);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push(opt);
    });
    return merged;
  }

  function resetDynamicFieldStates() {
    setFieldSearchValues({ ...initialDynamicFieldSearch });
    setFieldInputValues({ ...initialDynamicFieldInputValues });
  }

  const isPrefillingDynamicFields = useMemo(
    () =>
      Object.entries(fieldInitialLoadState).some(([key, isInitial]) => {
        if (!isInitial) {
          return false;
        }
        const selected = formData?.[key];
        return selected !== null && selected !== undefined && selected !== "";
      }),
    [fieldInitialLoadState, formData]
  );

  const computedIsLoading =
    isLoading || isHydratingDocument || optionsLoading || isPrefillingDynamicFields;

  return {
    allowSave,
    closeModal,
    errorArray,
    formData,
    handleChange,
    handleFileChange,
    handleSubmit,
    handleUploadSuccess,
    handleFieldSearchChange,
    fieldInputValues,
    hasChanged,
    initialFormData,
    isHydratingDocument,
    isLoading: computedIsLoading,
    options,
    requiredFields,
    resetForm,
    setUploadFailed,
    setUploadSuccess,
    supportedFiles,
    uploadFailed,
    uploadSuccess,
  };
};

export default useDocumentState;
